import type { Color, Config, Effect } from '@ml/config';
import { getCell } from '../board/grid.ts';
import { isBoosterLike, validatePath } from '../board/link.ts';
import { ensureMoves } from '../board/moves.ts';
import { applyGravity, refill, type FallEvent, type SpawnEvent } from '../board/spawn.ts';
import type { Board, BoosterKind, Cell, Coord } from '../board/types.ts';
import type { Rng } from '../rng.ts';
import {
  classifyRelease,
  linkContextOf,
  type Classification,
  type Hero,
  type HeroTurnState,
  type Skill,
} from './classify.ts';
import {
  abilityMainDamage,
  collateralDamage,
  essenceDamage,
  mergedCharge,
  normalMainDamage,
  type Element,
} from './damage.ts';
import { activatorOf, BoardFx, type Activator, type FxEvent } from './fx.ts';

export type ResolveConfig = Pick<
  Config,
  'board' | 'link' | 'booster' | 'essence' | 'combat' | 'debuff'
>;

/** Mục tiêu (monster) theo slot. HP ≤ 0 = đã chết. */
export type Target = {
  readonly hp: number;
  readonly def: number;
  readonly res: number;
  readonly element: Element;
};

export type ResolveOptions = {
  /** Kiểm chuỗi bằng validatePath trước khi phân loại (mặc định true). */
  readonly validate?: boolean;
  /** R8 refill (mặc định true). Tắt để golden so bảng "sau gravity, trước refill". */
  readonly refill?: boolean;
  /** R9 kiểm còn nước + xáo (mặc định true; chỉ chạy khi refill bật). */
  readonly ensureMoves?: boolean;
};

export type ResolveInput = {
  readonly board: Board;
  readonly path: readonly Coord[];
  readonly hero: Hero;
  readonly heroState: HeroTurnState;
  readonly targets: readonly Target[];
  /** Slot mục tiêu đang chọn; thiếu hoặc đã chết → `combat.defaultTarget`. */
  readonly target?: number;
  readonly rng: Rng;
  readonly config: ResolveConfig;
  readonly options?: ResolveOptions;
};

export type DamagePart = {
  readonly kind: 'main' | 'collateral' | 'essence';
  readonly target: number;
  readonly value: number;
  /** Các bước trung gian của công thức (để debug/hiện số). */
  readonly steps: readonly number[];
  /** false: mục tiêu đã chết trước phần này (`combat.overflowToNextTarget = false`). */
  readonly applied: boolean;
};

export type ResolveEvent =
  | FxEvent
  | { readonly type: 'classify'; readonly action: Classification['kind']; readonly slot?: number }
  | { readonly type: 'crit'; readonly crit: boolean }
  | { readonly type: 'randomTarget'; readonly target: number }
  | {
      readonly type: 'consume';
      readonly cells: readonly Coord[];
      readonly defused: readonly Coord[];
    }
  | {
      readonly type: 'merge';
      readonly from: readonly Coord[];
      readonly at: Coord;
      readonly charge: number;
    }
  | { readonly type: 'adjacent'; readonly at: Coord; readonly result: 'layerRemoved' | 'unlocked' }
  | { readonly type: 'boosterCreated'; readonly at: Coord; readonly cell: Cell }
  | { readonly type: 'damage'; readonly part: DamagePart }
  | { readonly type: 'fall'; readonly moves: readonly FallEvent[] }
  | { readonly type: 'spawn'; readonly cells: readonly SpawnEvent[] }
  | {
      readonly type: 'shuffle';
      readonly action: 'shuffled' | 'regenerated';
      readonly attempts: number;
    };

export type ResolveResult = {
  /** false: chuỗi hỏng hoặc phân loại huỷ — state giữ nguyên, không tốn lượt, không rút RNG. */
  readonly ok: boolean;
  readonly reason?: 'invalidPath' | 'cancel';
  readonly action: Classification;
  readonly consumesTurn: boolean;
  readonly crit: boolean;
  /** Bảng sau gravity, trước refill (golden-cases 0.2). */
  readonly boardAfterGravity: Board;
  /** Bảng cuối (sau refill + xáo nếu bật). */
  readonly board: Board;
  readonly targets: readonly Target[];
  readonly parts: readonly DamagePart[];
  /** Tổng các phần đã áp lên mục tiêu. */
  readonly total: number;
  readonly collateral: number;
  readonly gauge: number;
  readonly cooldowns: readonly number[];
  /** Hiệu ứng không lên bảng (heal, shield, stun, …) để pha battle (s6) áp. */
  readonly pendingEffects: readonly Effect[];
  readonly events: readonly ResolveEvent[];
};

function aliveIndices(targets: readonly Target[]): number[] {
  return targets.flatMap((t, i) => (t.hp > 0 ? [i] : []));
}

/** Mục tiêu mặc định (combat.md 3.5). Không còn ai sống → -1. */
export function defaultTarget(
  targets: readonly Target[],
  config: Pick<Config, 'combat'>,
  preferred?: number,
): number {
  if (preferred !== undefined && (targets[preferred]?.hp ?? 0) > 0) return preferred;
  const alive = aliveIndices(targets);
  if (alive.length === 0) return -1;
  if (config.combat.defaultTarget === 'lowestHp') {
    return alive.reduce((best, i) => (targets[i]!.hp < targets[best]!.hp ? i : best), alive[0]!);
  }
  return alive[0]!;
}

/** Booster tạo từ chuỗi (orbs.md 4.1). `coords` = phần không-ngọc-ấn của path, theo thứ tự. */
export function boosterForChain(
  coords: readonly Coord[],
  color: Color | null,
  config: Pick<Config, 'booster'>,
): Cell | null {
  const b = config.booster;
  const L = coords.length;
  if (L >= b.lightball.minLength) return { t: 'lightball' };
  if (color === null) return null; // chuỗi không màu chỉ có thể ra light ball
  const c = color;
  if (L >= b.bomb.minLength) return { t: 'booster', kind: 'bomb', color: c };
  if (L >= b.rocket.minLength) return { t: 'booster', kind: rocketDirection(coords), color: c };
  if (L === b.propeller.length && (!b.propeller.requiresSquare || hasSquare(coords))) {
    return { t: 'booster', kind: 'propeller', color: c };
  }
  return null;
}

export function hasSquare(coords: readonly Coord[]): boolean {
  const set = new Set(coords.map((c) => `${c.x},${c.y}`));
  return coords.some(
    ({ x, y }) =>
      set.has(`${x + 1},${y}`) && set.has(`${x},${y + 1}`) && set.has(`${x + 1},${y + 1}`),
  );
}

/** Hướng rocket: hộp bao rộng > cao → H; cao > rộng → V; hoà → bước cuối (dy=0 → H, dx=0 → V, chéo → H). */
export function rocketDirection(coords: readonly Coord[]): BoosterKind {
  const xs = coords.map((c) => c.x);
  const ys = coords.map((c) => c.y);
  const w = Math.max(...xs) - Math.min(...xs) + 1;
  const h = Math.max(...ys) - Math.min(...ys) + 1;
  if (w > h) return 'rocketH';
  if (h > w) return 'rocketV';
  const a = coords[coords.length - 2]!;
  const z = coords[coords.length - 1]!;
  return z.x === a.x && z.y !== a.y ? 'rocketV' : 'rocketH';
}

function skillOf(hero: Hero, slot: number): Skill {
  return slot === 3 ? hero.ultimate : hero.skills[slot]!;
}

const isAdj4 = (a: Coord, b: Coord) => Math.abs(a.x - b.x) + Math.abs(a.y - b.y) === 1;

/**
 * Resolve một lần thả chuỗi theo R0–R9 (orbs.md 6.1). Hàm thuần: không sửa đầu vào, RNG chỉ
 * rút qua `rng` theo đúng thứ tự combat.md 6 (R1 crit → mục tiêu random → R5 hiệu ứng → R6
 * propeller `any` / light ball `random` → R8 refill → R9 xáo).
 */
export function resolveRelease(input: ResolveInput): ResolveResult {
  const { board, path, hero, heroState, rng, config } = input;
  const opt = input.options ?? {};
  const ctx = linkContextOf(hero, heroState, config);
  const fail = (reason: 'invalidPath' | 'cancel', action: Classification): ResolveResult => ({
    ok: false,
    reason,
    action,
    consumesTurn: config.combat.invalidReleaseConsumesTurn,
    crit: false,
    boardAfterGravity: board,
    board,
    targets: input.targets,
    parts: [],
    total: 0,
    collateral: 0,
    gauge: heroState.gauge,
    cooldowns: heroState.cooldowns,
    pendingEffects: [],
    events: [],
  });
  const emptyAction: Classification = {
    kind: 'cancel',
    seq: [],
    L: 0,
    S: 0,
    lightballColors: new Map(),
  };

  // ---- R0 phân loại ----
  if ((opt.validate ?? true) && !validatePath(board, path, config, ctx).ok) {
    return fail('invalidPath', emptyAction);
  }
  const action = classifyRelease(board, path, hero, heroState, config);
  if (action.kind === 'cancel') return fail('cancel', action);

  const events: ResolveEvent[] = [{ type: 'classify', action: action.kind, slot: action.slot }];
  const fx = new BoardFx(board, rng, config);
  const isAttack =
    action.kind === 'NORMAL' || action.kind === 'ABILITY' || action.kind === 'ULTIMATE';
  const skill = action.slot !== undefined ? skillOf(hero, action.slot) : undefined;
  const stats = hero.stats;
  const critDmgPct = stats.critDmgPct ?? config.combat.critDmgPct;

  // ---- R1 crit ----
  let crit = false;
  if (isAttack) {
    crit = rng.nextInt(10000) < stats.critRateBp;
    events.push({ type: 'crit', crit });
  }
  let current = defaultTarget(input.targets, config, input.target);
  let randomTarget: number | undefined;
  if (skill?.target === 'random') {
    const alive = aliveIndices(input.targets);
    if (alive.length > 0) {
      randomTarget = alive[rng.nextInt(alive.length)]!;
      events.push({ type: 'randomTarget', target: randomTarget });
    }
  }

  // ---- R3 consume ----
  const cells = path.map((c) => getCell(board, c.x, c.y)!);
  const chainCoords: Coord[] = [];
  const essenceCoords: Coord[] = [];
  const boosters: { act: Activator; at: Coord }[] = [];
  const defused: Coord[] = [];
  cells.forEach((cell, i) => {
    const at = path[i]!;
    if (cell.t === 'essence') {
      essenceCoords.push(at);
      return;
    }
    chainCoords.push(at);
    if (cell.t === 'orb' && cell.overlay?.type === 'deadMark') defused.push(at);
    if (isBoosterLike(cell)) {
      const act = activatorOf(cell);
      boosters.push({
        act:
          act.t === 'lightball'
            ? { t: 'lightball', color: action.lightballColors.get(i) ?? null }
            : act,
        at,
      });
    }
    fx.set(at, null);
  });
  events.push({ type: 'consume', cells: chainCoords, defused });
  const consumed = [...chainCoords];
  if (essenceCoords.length >= config.essence.mergeMinCount) {
    const charges = essenceCoords.map(
      (c) => (getCell(board, c.x, c.y) as Cell & { t: 'essence' }).charge,
    );
    const at = essenceCoords[essenceCoords.length - 1]!;
    const charge = mergedCharge(charges, config);
    for (const c of essenceCoords) fx.set(c, null);
    fx.set(at, { t: 'essence', charge });
    fx.shielded.add(fx.idx(at));
    consumed.push(...essenceCoords);
    events.push({ type: 'merge', from: essenceCoords, at, charge });
  }
  if (config.booster.comboEnabled && boosters.length >= 2) {
    const [a, b, ...rest] = boosters;
    fx.queue.push({ k: 'combo', a: a!.act, b: b!.act, at: b!.at });
    for (const r of rest) fx.queue.push({ k: 'single', act: r.act, at: r.at });
  } else {
    for (const r of boosters) fx.queue.push({ k: 'single', act: r.act, at: r.at });
  }
  const dcfg = config.debuff;
  // orbs 3.4: phá băng / mở khoá kề cạnh chỉ áp cho NORMAL/ABILITY/ULTIMATE/MERGE.
  const adjacentRule = action.kind !== 'BOOSTER_TAP' && action.kind !== 'BOOSTER_COMBO';
  for (const at of adjacentRule ? fx.coords((cell) => cell.t === 'orb' && !!cell.overlay) : []) {
    if (!consumed.some((c) => isAdj4(c, at))) continue;
    const cell = fx.get(at) as Cell & { t: 'orb' };
    const o = cell.overlay!;
    const bare: Cell = { t: 'orb', kind: cell.kind, color: cell.color };
    if (o.type === 'freeze' && dcfg.freeze.adjacentChainHits) {
      fx.set(
        at,
        o.layers > 1 ? { ...bare, overlay: { type: 'freeze', layers: o.layers - 1 } } : bare,
      );
      events.push({ type: 'adjacent', at, result: 'layerRemoved' });
    } else if (o.type === 'lock' && dcfg.lock.adjacentChainUnlock) {
      fx.set(at, bare);
      events.push({ type: 'adjacent', at, result: 'unlocked' });
    }
  }

  // ---- R4 booster mới ----
  const anchor = chainCoords[chainCoords.length - 1];
  const spawnOk =
    (action.kind === 'NORMAL' && config.booster.spawnFromNormal) ||
    ((action.kind === 'ABILITY' || action.kind === 'ULTIMATE') && config.booster.spawnFromAbility);
  if (spawnOk && anchor) {
    const color =
      action.kind === 'NORMAL'
        ? (action.color ?? null)
        : skill!.element === 'none'
          ? null
          : skill!.element;
    const made = boosterForChain(chainCoords, color, config);
    if (made) {
      fx.set(anchor, made);
      if (config.booster.newBoosterShielded) fx.shielded.add(fx.idx(anchor));
      events.push({ type: 'boosterCreated', at: anchor, cell: made });
    }
  }

  // ---- R5 hiệu ứng skill lên bảng ----
  const pendingEffects: Effect[] = [];
  if (skill && anchor) {
    for (const e of skill.effects) {
      switch (e.type) {
        case 'hitRow':
          fx.hitAll(fx.rows(anchor.y, anchor.y));
          break;
        case 'hitColumn':
          fx.hitAll(fx.cols(anchor.x, anchor.x));
          break;
        case 'hitArea':
          fx.hitAll(fx.square(anchor, e.radius));
          break;
        case 'hitRandom':
          fx.hitRandom(e.count);
          break;
        case 'convertColor':
          fx.convertColor(e.count, e.to, e.from);
          break;
        case 'spawnBooster':
          fx.spawnBooster(e.booster, e.count);
          break;
        case 'cleanse':
          fx.cleanse(e.count);
          break;
        case 'detonateEssence':
          fx.detonateEssence(e.count);
          break;
        case 'damage':
          break; // phần chính đã khai qua powerPct của skill (combat.md 4.3)
        default:
          pendingEffects.push(e);
      }
    }
  }

  // ---- R6 hàng đợi kích hoạt ----
  fx.runQueue();
  events.push(...fx.events);

  // ---- R7 sát thương + thanh ult ----
  const hp = input.targets.map((t) => t.hp);
  const parts: DamagePart[] = [];
  const apply = (
    kind: DamagePart['kind'],
    target: number,
    value: number,
    steps: readonly number[],
  ) => {
    let t = target;
    if (t >= 0 && hp[t]! <= 0 && config.combat.overflowToNextTarget) {
      t = defaultTarget(
        input.targets.map((x, i) => ({ ...x, hp: hp[i]! })),
        config,
      );
    }
    const applied = t >= 0 && hp[t]! > 0;
    if (applied) hp[t] = Math.max(0, hp[t]! - value);
    const part: DamagePart = { kind, target: t, value, steps, applied };
    parts.push(part);
    events.push({ type: 'damage', part });
  };
  if (isAttack && current >= 0) {
    if (action.kind === 'NORMAL') {
      const tgt = input.targets[current]!;
      const d = normalMainDamage(
        {
          handAttack: stats.handAttack,
          length: action.L,
          color: action.color ?? 'none',
          crit,
          critDmgPct,
          targetElement: tgt.element,
          targetDef: tgt.def,
        },
        config,
      );
      apply('main', current, d.value, d.steps);
    } else if (skill!.powerPct > 0) {
      const list =
        skill!.target === 'all'
          ? aliveIndices(input.targets)
          : [skill!.target === 'random' ? randomTarget! : current];
      for (const ti of list) {
        const tgt = input.targets[ti]!;
        const d = abilityMainDamage(
          {
            abilityAttack: stats.abilityAttack,
            powerPct: skill!.powerPct,
            element: skill!.element,
            crit,
            critDmgPct,
            targetElement: tgt.element,
            targetRes: tgt.res,
          },
          config,
        );
        apply('main', ti, d.value, d.steps);
      }
    }
  }
  current = current >= 0 ? current : defaultTarget(input.targets, config);
  if (fx.collateral > 0 && current >= 0) {
    const v = collateralDamage(
      stats.handAttack,
      fx.collateral,
      input.targets[current]!.def,
      config,
    );
    apply('collateral', current, v, [fx.collateral]);
  }
  for (const det of fx.detonations) {
    if (current < 0) break;
    const d = essenceDamage(stats.abilityAttack, det.charge, input.targets[current]!.res, config);
    apply('essence', current, d.value, d.steps);
  }
  const total = parts.reduce((s, p) => s + (p.applied ? p.value : 0), 0);
  const u = config.combat.ult;
  const gauge =
    action.kind === 'ULTIMATE'
      ? 0
      : Math.min(
          u.gaugeMax,
          heroState.gauge +
            chainCoords.length * u.gainPerPathOrb +
            fx.collateral * u.gainPerCollateral,
        );
  const cooldowns = heroState.cooldowns.slice();
  if (action.kind === 'ABILITY') cooldowns[action.slot!] = skill!.cooldownTurns;

  // ---- R8 gravity + refill, R9 còn nước ----
  const g = applyGravity(fx.board());
  if (g.moves.length) events.push({ type: 'fall', moves: g.moves });
  let finalBoard = g.board;
  if (opt.refill ?? true) {
    const r = refill(g.board, rng, config);
    if (r.spawned.length) events.push({ type: 'spawn', cells: r.spawned });
    finalBoard = r.board;
    if (opt.ensureMoves ?? true) {
      const nextCtx = linkContextOf(hero, { cooldowns, gauge }, config);
      const em = ensureMoves(finalBoard, rng, config, nextCtx);
      if (em.action !== 'none')
        events.push({ type: 'shuffle', action: em.action, attempts: em.attempts });
      finalBoard = em.board;
    }
  }

  return {
    ok: true,
    action,
    consumesTurn: action.kind === 'MERGE' ? config.essence.mergeOnlyConsumesTurn : true,
    crit,
    boardAfterGravity: g.board,
    board: finalBoard,
    targets: input.targets.map((t, i) => ({ ...t, hp: hp[i]! })),
    parts,
    total,
    collateral: fx.collateral,
    gauge,
    cooldowns,
    pendingEffects,
    events,
  };
}
